import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
  Input, Label, Button, Switch,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@werkstatt/ui';
import { useToast } from '../../hooks/use-toast';
import { getCsrfToken } from '../../lib/api';
import { AlertTriangle, CheckCircle2, Eye, EyeOff, Info, Loader2, PlugZap } from 'lucide-react';

interface ImapSettings {
  imap_enabled: boolean;
  imap_host: string | null;
  imap_port: number;
  imap_user: string | null;
  imap_security: 'ssl' | 'starttls' | 'none';
  imap_folder: string;
  has_password: boolean;
  imap_verified_at: string | null;
  imap_last_polled_at: string | null;
  imap_last_uid: number | null;
}

interface ImapForm {
  imap_enabled: boolean;
  imap_host: string;
  imap_port: string;
  imap_user: string;
  imap_password: string;
  imap_security: 'ssl' | 'starttls' | 'none';
  imap_folder: string;
}

const defaultForm: ImapForm = {
  imap_enabled: false,
  imap_host: '',
  imap_port: '993',
  imap_user: '',
  imap_password: '',
  imap_security: 'ssl',
  imap_folder: 'INBOX',
};

const SECURITY_OPTIONS: { value: ImapForm['imap_security']; label: string; port: number }[] = [
  { value: 'ssl', label: 'SSL/TLS (Port 993)', port: 993 },
  { value: 'starttls', label: 'STARTTLS (Port 143)', port: 143 },
  { value: 'none', label: 'Unverschlüsselt (nicht empfohlen)', port: 143 },
];

function formatDate(value: string | null): string {
  if (!value) return '–';
  return new Date(value).toLocaleString('de-DE');
}

export default function ImapSettings() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [form, setForm] = useState<ImapForm>(defaultForm);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordChanged, setPasswordChanged] = useState(false);

  const { data: settings, isLoading } = useQuery({
    queryKey: ['imap-settings'],
    queryFn: async () => {
      const response = await fetch('/api/settings/imap', { credentials: 'include' });
      if (!response.ok) throw new Error('Fehler beim Laden der IMAP-Einstellungen');
      return response.json() as Promise<ImapSettings>;
    },
  });

  useEffect(() => {
    if (settings) {
      setForm({
        imap_enabled: settings.imap_enabled,
        imap_host: settings.imap_host ?? '',
        imap_port: String(settings.imap_port),
        imap_user: settings.imap_user ?? '',
        imap_password: '',
        imap_security: settings.imap_security,
        imap_folder: settings.imap_folder,
      });
      setPasswordChanged(false);
    }
  }, [settings]);

  const updateMutation = useMutation({
    mutationFn: async (data: ImapForm) => {
      const payload: Record<string, unknown> = {
        imap_enabled: data.imap_enabled,
        imap_host: data.imap_host || null,
        imap_port: parseInt(data.imap_port, 10) || 993,
        imap_user: data.imap_user || null,
        imap_security: data.imap_security,
        imap_folder: data.imap_folder || 'INBOX',
      };
      if (passwordChanged) {
        payload.imap_password = data.imap_password;
      }
      const response = await fetch('/api/settings/imap', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Speichern fehlgeschlagen');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['imap-settings'] });
      setPasswordChanged(false);
      setForm(prev => ({ ...prev, imap_password: '' }));
      toast({ title: 'IMAP-Einstellungen gespeichert' });
    },
    onError: (error: Error) => {
      toast({ variant: 'destructive', title: 'Fehler', description: error.message });
    },
  });

  const testMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch('/api/settings/imap/test', {
        method: 'POST',
        headers: { 'X-CSRF-Token': getCsrfToken() },
        credentials: 'include',
      });
      return response.json() as Promise<{ success: boolean; message: string }>;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['imap-settings'] });
      if (result.success) {
        toast({ title: 'Verbindung erfolgreich', description: result.message });
      } else {
        toast({ variant: 'destructive', title: 'Verbindung fehlgeschlagen', description: result.message });
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate(form);
  };

  if (isLoading) {
    return (
      <Card variant="glass">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card variant="glass">
        <CardHeader>
          <CardTitle className="font-display flex items-center gap-2">
            <PlugZap className="h-5 w-5" />
            Postfachabruf (IMAP)
          </CardTitle>
          <CardDescription>
            Kundenantworten werden automatisch abgerufen und im Postfach dem
            passenden Vorgang zugeordnet.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <label className="flex items-start justify-between gap-4 rounded-md border border-border px-3 py-2.5">
              <span className="text-sm">
                Postfachabruf aktiv
                <span className="block text-xs text-muted-foreground">
                  Ohne aktive Verbindung werden keine eingehenden Mails abgerufen.
                </span>
              </span>
              <Switch
                checked={form.imap_enabled}
                onCheckedChange={(value) => setForm({ ...form, imap_enabled: value })}
                aria-label="Postfachabruf aktiv"
              />
            </label>

            <div className="grid gap-4 md:grid-cols-3">
              <div className="md:col-span-2">
                <Label htmlFor="imap_host">IMAP-Server</Label>
                <Input
                  id="imap_host"
                  value={form.imap_host}
                  onChange={e => setForm({ ...form, imap_host: e.target.value })}
                  placeholder="imap.example.com"
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="imap_port">Port</Label>
                <Input
                  id="imap_port"
                  type="number"
                  value={form.imap_port}
                  onChange={e => setForm({ ...form, imap_port: e.target.value })}
                  className="mt-1.5"
                />
              </div>
            </div>

            <div>
              <Label>Verschlüsselung</Label>
              <Select
                value={form.imap_security}
                onValueChange={v => {
                  const option = SECURITY_OPTIONS.find(o => o.value === v);
                  setForm({
                    ...form,
                    imap_security: v as ImapForm['imap_security'],
                    imap_port: option ? String(option.port) : form.imap_port,
                  });
                }}
              >
                <SelectTrigger className="mt-1.5 w-full md:w-64"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SECURITY_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label htmlFor="imap_user">Benutzername</Label>
                <Input
                  id="imap_user"
                  value={form.imap_user}
                  onChange={e => setForm({ ...form, imap_user: e.target.value })}
                  placeholder="user@example.com"
                  autoComplete="off"
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="imap_password">
                  Passwort{settings?.has_password && ' (gesetzt — leer lassen um beizubehalten)'}
                </Label>
                <div className="relative mt-1.5">
                  <Input
                    id="imap_password"
                    type={showPassword ? 'text' : 'password'}
                    value={form.imap_password}
                    onChange={e => {
                      setForm({ ...form, imap_password: e.target.value });
                      setPasswordChanged(true);
                    }}
                    placeholder={settings?.has_password ? '••••••••' : 'Passwort eingeben'}
                    autoComplete="new-password"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="imap_folder">Ordner</Label>
              <Input
                id="imap_folder"
                value={form.imap_folder}
                onChange={e => setForm({ ...form, imap_folder: e.target.value })}
                placeholder="INBOX"
                className="mt-1.5 w-full md:w-64"
              />
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                {settings?.imap_verified_at ? (
                  <span className="flex items-center gap-1.5">
                    <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                    Verbindung geprüft: {formatDate(settings.imap_verified_at)}
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    Verbindung noch nicht geprüft
                  </span>
                )}
                {settings?.imap_last_polled_at && (
                  <span className="flex items-center gap-1.5">
                    <Info className="h-3.5 w-3.5" />
                    Letzter Abruf: {formatDate(settings.imap_last_polled_at)}
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  disabled={testMutation.isPending}
                  onClick={() => testMutation.mutate()}
                >
                  {testMutation.isPending
                    ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    : <PlugZap className="mr-2 h-4 w-4" />}
                  Verbindung testen
                </Button>
                <Button type="submit" disabled={updateMutation.isPending}>
                  {updateMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Speichern
                </Button>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
